import { BarChart3, Home, LogOut, Menu, ShieldCheck, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useWebAuth } from '../contexts/WebAuthContext';
import { ThemeToggleButton } from './ThemeToggleButton';

const navItems = [
  { path: '/admin', label: 'ダッシュボード', icon: Home, end: true },
  { path: '/admin/users', label: 'ユーザー', icon: Users, end: false },
];

/**
 * 管理画面共通レイアウト
 *
 * サイドナビゲーションとヘッダーを提供する
 */
export function AdminLayout() {
  const { user, signOut } = useWebAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // ルート変更時にサイドバーを閉じる
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.pathnameの変更を検知してサイドバーを閉じる意図的な設計
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // サイドバーのコンテンツ
  const sidebarContent = (
    <>
      {/* ロゴ */}
      <div className="p-6 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-primary-500" />
          <div>
            <h1 className="text-lg font-bold text-sidebar-foreground">管理画面</h1>
            <div className="flex items-center gap-1.5">
              <p className="text-xs text-muted-foreground">きゅーもAI エージェント v2</p>
              {import.meta.env.VITE_APP_ENVIRONMENT &&
                import.meta.env.VITE_APP_ENVIRONMENT !== 'prod' && (
                  <span className="px-1 py-px text-[9px] font-medium rounded bg-orange-500 text-white">
                    {import.meta.env.VITE_APP_ENVIRONMENT.toUpperCase()}
                  </span>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* ナビゲーション */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* ユーザー情報・テーマ切替 */}
      <div className="p-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
            {user?.email?.charAt(0).toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-sidebar-foreground truncate">{user?.email || 'Unknown'}</p>
            <p className="text-xs text-muted-foreground">管理者</p>
          </div>
          <ThemeToggleButton />
        </div>
        <Link
          to="/account/security"
          className="mt-2 w-full flex items-center gap-3 px-4 py-2 text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors"
        >
          <ShieldCheck className="w-5 h-5" />
          <span>認証アプリの登録</span>
        </Link>
        <button
          type="button"
          onClick={handleSignOut}
          className="mt-2 w-full flex items-center gap-3 px-4 py-2 text-muted-foreground hover:text-red-400 hover:bg-sidebar-accent rounded-lg transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>ログアウト</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* モバイルヘッダー */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-sidebar border-b border-sidebar-border px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setIsSidebarOpen(true)}
          className="p-2 text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors"
          aria-label="メニューを開く"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-primary-500" />
          <span className="text-sidebar-foreground font-bold">管理画面</span>
        </div>
      </div>

      {/* モバイルサイドバーオーバーレイ */}
      {isSidebarOpen && (
        <button
          type="button"
          aria-label="メニューを閉じる"
          className="md:hidden fixed inset-0 z-40 bg-black/50 cursor-default"
          onClick={() => setIsSidebarOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsSidebarOpen(false)}
        />
      )}

      {/* モバイルサイドバー */}
      <aside
        className={`md:hidden fixed top-0 left-0 z-50 w-64 h-screen bg-sidebar border-r border-sidebar-border flex flex-col transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* 閉じるボタン */}
        <div className="absolute top-4 right-4">
          <button
            type="button"
            onClick={() => setIsSidebarOpen(false)}
            className="p-2 text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors"
            aria-label="メニューを閉じる"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {sidebarContent}
      </aside>

      {/* デスクトップサイドバー */}
      <aside className="hidden md:flex w-64 h-screen sticky top-0 bg-sidebar border-r border-sidebar-border flex-col">
        {sidebarContent}
      </aside>

      {/* メインコンテンツ */}
      <main className="flex-1 overflow-auto pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
